import { inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { ProjectService } from '../project/project.service';
import { EnumLanguage } from '../../enums/language.enum';

export interface IProjectUser {
  id: string;
  fullName: string;
  email: string;
  role: string;
  createdAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly apiService = inject(ApiService);
  private readonly projectService = inject(ProjectService);

  async getProjectUsers(): Promise<IProjectUser[]> {
    const projectId = this.projectService.getProjectId();
    if (!projectId) {
      throw new Error('No project selected');
    }
    const response = await this.apiService.get<IProjectUser[]>(`/users/project/${projectId}`);
    return response.data;
  }

  async getCurrentUserInProject(userId: string): Promise<IProjectUser | null> {
    const projectId = this.projectService.getProjectId();
    if (!projectId) {
      throw new Error('No project selected');
    }
    const response = await this.apiService.get<IProjectUser>(`/users/project/${projectId}/user/${userId}`);
    return response.data;
  }

  async getCurrentUserInProjectByEmail(email: string): Promise<IProjectUser | null> {
    const projectId = this.projectService.getProjectId();
    if (!projectId) {
      throw new Error('No project selected');
    }
    const response = await this.apiService.get<IProjectUser>(`/users/project/${projectId}/user-by-email/${email}`);
    return response.data;
  }

  async removeUserFromProject(userId: string): Promise<void> {
    const projectId = this.projectService.getProjectId();
    if (!projectId) {
      throw new Error('No project selected');
    }
    await this.apiService.delete<void>(`/users/project/${projectId}/user/${userId}`);
  }

  async createUserInvitation(email: string, roleId: string): Promise<void> {
    await this.apiService.post<void>('/auth/invite', { body: { email, roleId } });
  }

  async leaveProject(projectId: string): Promise<void> {
    await this.apiService.delete<void>(`/users/project/${projectId}/leave`, { showAPIError: false });
    this.projectService.projectListChanged$.next();
  }

  async updateLanguage(language: EnumLanguage): Promise<void> {
    await this.apiService.patch<void>('/users/me/language', { body: { language }, showAPIError: false });
  }
}
